import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Row, Schedule, SequenceField, NotifyStep, SkipLine, openInBuilder, clampInt, Need } from "./SetupFlow";

const CreateAccountProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [accountListId, setAccountListId] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKLY | MANUAL
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    agentsData.fetchAccountLists();
  }, []);

  const canSubmit = !!accountListId && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Target accounts → people",
    accountListId,
    titles: titles.trim() || undefined,
    perCompany: clampInt(perCompany, 1, null, 5),
    sequenceId,
    dailyCap: clampInt(dailyCap, 1, null, 25),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createAccountProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "account-prospector", buildPayload(), setSaving);

  return (
    <SetupPage
      title="Hire · Target accounts → people"
      name={{ value: name, onChange: setName, placeholder: "Target accounts → people" }}
      bar={{ canSubmit, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"), reason: "Pick an account list and a sequence to create it." }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={setRunWhen} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} />
      </When>
      <Step icon="building" tone="green" title="Take accounts from">
        <Field hint={(agentsData.accountLists || []).length ? null : "None yet. Build an account list first."}>
          <Need ok={!!accountListId}><AgentSelect searchable placeholder="Pick an account list" value={accountListId}
            onChange={setAccountListId}
            options={(agentsData.accountLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))} /></Need>
        </Field>
      </Step>
      <Step icon="user" tone="violet" title="Find decision-makers there">
        <Row>
          <Field label="Job titles">
            <input className="ag-input" value={titles} placeholder="Anyone, e.g. VP Sales" onChange={e => setTitles(e.target.value)} />
          </Field>
          <Field label="People per account">
            <input className="ag-input" type="number" min="1" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </Field>
          <Field label="People per run">
            <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
          </Field>
        </Row>
      </Step>
      <Step icon="mail" tone="violet" title="Find their emails" tags={[{ label: "For email sequences" }]} />
      <Step icon="send" tone="amber" title="Add to a sequence">
        <SequenceField id={sequenceId} name={sequenceName} onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }} />
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
      <SkipLine />
    </SetupPage>
  );
});

export default CreateAccountProspector;
